// Reports view — "Adherence by day of week".
// Simplest of the 3 Reports charts: one bar per weekday (buildWeekdayAdherenceSeries)
// plus a single reference line (the period's overall average) — the bar itself
// is self-explanatory from the title/subtitle+axis, but the ref line is a second
// visual series and needs its own legend entry, same as the other 2 Reports charts.
//
// No-logged-days treatment mirrors ReportsOverUnderChart.js's sibling chart
// (same weekday-bucketed shape, same n:0 possibility per weekday): a dimmed
// axis tick, a tooltip that explains the missing bar instead of showing
// nothing, and a centered message when the whole period comes back empty.

import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";

import { CHART_THEME } from "./viewTokens";
import { ChartLegend, TooltipShell } from "./viewAtoms";
import { useCenteredTooltip } from "../../../../../hooks/useCenteredTooltip";

const BAR_COLOR = "var(--gradient-left)";
const REF_COLOR = "var(--gradient-right)";
// Theme-aware, shared with the other 2 Reports charts — see CHART_THEME
// (viewTokens.js) for the full "why".
const { tick: CHART_TICK, tickFontSize: CHART_TICK_SIZE, grid: CHART_GRID } = CHART_THEME;
// Dimmer than CHART_TICK — flags a weekday with zero logged days directly on
// the axis, without needing a hover to find out. Same token/reasoning as
// ReportsOverUnderChart.js's MUTED_TICK_COLOR.
const MUTED_TICK_COLOR = "var(--white-50)";

const rw = (t, key, opts) => t(`nutritionPage.progress.reports.weekdayChart.${key}`, opts);

/* ---- custom tooltip ------------------------------------------------------ */

const WeekdayTooltip = ({ active, payload, onMeasureWidth }) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <TooltipShell active={active} payload={payload} onMeasureWidth={onMeasureWidth} isEmpty={(pt) => !pt}>
      {(pt) =>
        pt.n === 0 ? (
          <>
            <div className="np-reports__tooltip-title">{pt.weekday}</div>
            <div className="np-reports__tooltip-label">{rw(t, "tooltipNoLoggedDays")}</div>
          </>
        ) : (
          <>
            <div className="np-reports__tooltip-title">
              {rw(t, "tooltipTitle", { weekday: pt.weekday, count: pt.n })}
            </div>
            <div className="np-hero__tooltip-row">
              <span className="np-reports__tooltip-label">{rw(t, "tooltipLabel")}</span>
              <span style={{ color: BAR_COLOR }}>{pt.pctOffTarget}%</span>
            </div>
            {/* "mixed" isn't an actionable finding the way over/under are —
                it just means the days split roughly evenly, which "192 kcal"
                on its own already doesn't overclaim. So the qualifier only
                renders for a genuinely consistent direction; silence, not a
                vague label, is what stands in for "no clear pattern". */}
            {pt.avgKcalOff != null && (
              <div className="np-hero__tooltip-row">
                <span className="np-reports__tooltip-label">{rw(t, "tooltipKcalOff")}</span>
                <span style={{ color: BAR_COLOR }}>
                  {pt.kcalDirection === "mixed"
                    ? rw(t, "kcalDay", { value: pt.avgKcalOff.toLocaleString() })
                    : rw(t, "tooltipKcalOffValue", {
                        value: pt.avgKcalOff.toLocaleString(),
                        direction: rw(t, `kcalDirection.${pt.kcalDirection}`),
                      })}
                </span>
              </div>
            )}
          </>
        )
      }
    </TooltipShell>
  );
};

WeekdayTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  onMeasureWidth: PropTypes.func,
};

/* ---- chart ----------------------------------------------------------------- */

export const ReportsWeekdayChart = ({ series }) => {
  const { t } = useTranslation("athlete-portal");
  // Day-weighted mean of the 7 weekday means — equals the overall mean of
  // dayDeviation across every logged day (weekdayGroups partitions the full
  // set, one day in exactly one group), computed here from `series` alone
  // rather than re-reading dayRows a second time. Uses pctOffTargetRaw
  // (unrounded), NOT the displayed pctOffTarget — averaging 7 already-rounded
  // per-weekday means can disagree with the true overall mean by ±1 (same
  // double-rounding issue buildMacroBreakdownSeries had for its kcal split);
  // this way only rounds once, on the final result.
  const overallPct = useMemo(() => {
    const totalN = series.reduce((s, d) => s + d.n, 0);
    if (!totalN) return null;
    const weightedSum = series.reduce((s, d) => s + (d.pctOffTargetRaw ?? 0) * d.n, 0);
    return Math.round(weightedSum / totalN);
  }, [series]);

  // Manual tooltip centering — see useCenteredTooltip's own header comment
  // for the full "why" (Recharts doesn't do this by default).
  const { containerRef, tooltipPosition, onMeasureWidth, chartHandlers } = useCenteredTooltip({
    initialWidth: 140,
  });

  // Legend payload for the reference line (the period's overall average).
  // The average's own value goes in the label, not just the word "average":
  // it matches the height the line sits at, so the reader maps label to line
  // without having to infer what it's the average OF (the y-axis unit).
  const legendPayload = useMemo(
    () => [
      {
        value: rw(t, "legendAverage", { value: overallPct }),
        color: REF_COLOR,
        dataKey: "average",
        swatchShape: "dashedLine",
      },
    ],
    [t, overallPct],
  );

  // Whole selected period has no logged days at all — every entry is n:0,
  // so no bars render (plain <Bar>, no custom shape, so Recharts already
  // skips null-valued points on its own — see ReportsOverUnderChart.js's
  // OverUnderBar comment for the case where that ISN'T true). Axes/grid
  // still render normally; only the plot area gets a centered explanation.
  const isEmptyPeriod = useMemo(() => series.every((d) => !d.n), [series]);

  // Dims a weekday's own axis label when it has zero logged days, mirroring
  // ReportsOverUnderChart.js's renderWeekdayTick — see that comment for why
  // this reads series[index] directly instead of matching on payload.value.
  const renderWeekdayTick = ({ x, y, payload, index }) => {
    const point = series[index];
    return (
      <text
        x={x}
        y={y + 12}
        textAnchor="middle"
        fontSize={CHART_TICK_SIZE}
        fill={point?.n ? CHART_TICK : MUTED_TICK_COLOR}
      >
        {payload.value}
      </text>
    );
  };

  return (
    <div className="np-weekday-chart__plot" ref={containerRef}>
      <ResponsiveContainer width="99%" height={280}>
        <BarChart data={series} margin={{ top: 8, right: 16, bottom: 0, left: 0 }} {...chartHandlers}>
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <XAxis dataKey="weekday" tick={renderWeekdayTick} tickLine={false} axisLine={false} />
          <YAxis
            tick={{ fontSize: CHART_TICK_SIZE, fill: CHART_TICK }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => `${v}%`}
            width={44}
          />
          <Tooltip
            content={<WeekdayTooltip onMeasureWidth={onMeasureWidth} />}
            cursor={false}
            position={tooltipPosition}
          />
          {overallPct != null && (
            <Legend
              content={<ChartLegend coloredText />}
              payload={legendPayload}
              wrapperStyle={{ paddingTop: 8 }}
            />
          )}

          {overallPct != null && (
            <ReferenceLine y={overallPct} stroke={REF_COLOR} strokeDasharray="4 2" />
          )}

          <Bar dataKey="pctOffTarget" fill={BAR_COLOR} radius={[8, 8, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
      {isEmptyPeriod && <div className="np-reports__empty-msg">{rw(t, "emptyPeriod")}</div>}
    </div>
  );
};

ReportsWeekdayChart.propTypes = {
  series: PropTypes.array.isRequired,
};

export default ReportsWeekdayChart;
